import type { ConversationKind } from '@rp/contracts'
import { useNavigate } from '@tanstack/react-router'
import { Button, StatusBar } from '../../design'
import { REPLY_COPY } from './copy'
import { guidanceForKind } from './failure'
import type { SendState } from './sendState'

type Sent = Extract<SendState, { kind: 'sent' }>
type Failed = Extract<SendState, { kind: 'failed' }>
type Rival = Extract<SendState, { kind: 'rival' }>

const SentNotice = ({ state }: { state: Sent }) => (
  <StatusBar
    tone="success"
    title={REPLY_COPY.sent}
    {...(state.already ? { detail: REPLY_COPY.alreadySent } : {})}
    {...(state.outcome === null ? {} : { note: REPLY_COPY.recordedOutcome[state.outcome] })}
  />
)

const PublishingNotice = ({ already }: { already: boolean }) => (
  <StatusBar tone="neutral" title={already ? REPLY_COPY.alreadyPublishing : REPLY_COPY.publishing} />
)

const RivalNotice = ({ state }: { state: Rival }) => {
  const { reply } = state
  const sent = reply.status === 'published'
  return (
    <StatusBar
      tone="warning"
      live="alert"
      title={sent ? REPLY_COPY.rivalSent(reply.postedByName) : REPLY_COPY.rivalSending(reply.postedByName)}
      detail={sent ? REPLY_COPY.rivalSentText(reply.text) : REPLY_COPY.rivalSendingText(reply.text)}
    />
  )
}

type FailureNoticeProps = { state: Failed; kind: ConversationKind; onRetry: () => void }

const FailureNotice = ({ state, kind, onRetry }: FailureNoticeProps) => {
  const navigate = useNavigate()
  const guidance = guidanceForKind(state.guidance, kind)
  const reconnect = guidance.actionKind === 'reconnect'
  const action =
    guidance.actionKind === null ? undefined : (
      <Button variant={reconnect ? 'primary' : 'secondary'} onClick={() => (reconnect ? void navigate({ to: '/settings' }) : onRetry())}>
        {guidance.actionLabel}
      </Button>
    )
  return (
    <StatusBar
      tone="danger"
      live="alert"
      title={guidance.title}
      detail={guidance.nextStep}
      {...(action === undefined ? {} : { action })}
    />
  )
}

type ReplySendStatusProps = { state: SendState; kind: ConversationKind; onRetry: () => void }

export const ReplySendStatus = ({ state, kind, onRetry }: ReplySendStatusProps) => {
  if (state.kind === 'failed') return <FailureNotice state={state} kind={kind} onRetry={onRetry} />
  if (state.kind === 'rival') return <RivalNotice state={state} />
  if (state.kind === 'sent') return <SentNotice state={state} />
  if (state.kind === 'publishing') return <PublishingNotice already={state.already} />
  return null
}
